import moment from 'moment';

import { post, get, put, patch, getDownload } from '../utils/api';
import { formatDateLocale } from '../utils/date-helpers';

type ApplicationsNeedingInfoResponseType = {
  propertyId: string,
  count: number,
  applications: Array<{
    applicationId: string,
    applicationCode: ?number,
    firstName: ?string,
    lastName: ?string,
    reason: string,
  }>,
};

type AddToWaitlistPayloadType = {
  affordableProgram: string,
  anticipatedAnnualIncome: number,
  incomeLevel: string,
  requestingAccommodations: boolean,
  preferences: Array<string>,
};

type AddWaitlistNotePayloadType = {
  description: string,
  applicantContacted: boolean,
  affordableWaitlistRow: AddToWaitlistPayloadType & {
    applicationStatusId: string,
    id: string,
  },
};

type SkipFromWaitlistReasonType =
  | 'urgentHousingNeed'
  | 'medicalNecessity'
  | 'incomeTargeting'
  | 'preferences'
  | 'otherSeeNotes';

type SkipFromWaitlistPayloadType = {
  description: string,
  reasons: SkipFromWaitlistReasonType[],
  affordableWaitlistRow: AddToWaitlistPayloadType & {
    applicationStatusId: string,
    id: string,
    applicationId: string,
    position: number,
  },
};

type ReinstateToWaitlistReasonType =
  | 'changedMind'
  | 'unintentionallyRemoved'
  | 'resolvedScreeningIssue'
  | 'otherSeeNotes';

type ReinstateToWaitlistPayloadType = {
  description: string,
  reasons: ReinstateToWaitlistReasonType[],
  reinstatePosition: 'originalPlace' | 'bottomOfWaitlist',
  affordableWaitlistRow: AddToWaitlistPayloadType & {
    applicationStatusId: string,
    id: string,
    applicationId: string,
    position: number,
  },
};

// Shared by denyFromWaitlist/cancelFromWaitlist (ENG-29579/ENG-29577) — the
// backend validates `reasons` against a fixed literal list per endpoint (the
// exact display strings, not camelCased codes), `description` is optional.
type AffordableWaitlistPositionActionPayloadType = {
  reasons: Array<string>,
  description?: string,
};

// Flat response shape shared by deny/cancel/select/reinstate — the backend
// dropped the { message, data: {...} } wrapper these four endpoints used to
// return.
type AffordableWaitlistPositionActionResponseType = {
  affordableWaitlistId: string,
  applicationId: string,
};

type DenyFromWaitlistResponseType =
  AffordableWaitlistPositionActionResponseType & {
    status: 'DENIED',
  };

type CancelFromWaitlistResponseType =
  AffordableWaitlistPositionActionResponseType & {
    status: 'CANCELLED',
  };

type SelectFromWaitlistResponseType =
  AffordableWaitlistPositionActionResponseType & {
    status: 'SELECTED',
    cancelledPositionsCount: number,
  };

type ReinstateFromWaitlistResponseType =
  AffordableWaitlistPositionActionResponseType & {
    status: 'WAITING',
    restoredPositionsCount: number,
  };

// The add route answers with the same AffordableWaitlistEditResult edit and
// reinstate return, and never reinstates. A New Portal App selection with no
// Waitlist Program moves the application to In Process instead of creating a
// record, so it answers with movedToInProcess and no affordableWaitlistId.
type AddToWaitlistV2ResponseType = {
  applicationId: string,
  affordableWaitlistId: ?string,
  addedBedrooms: ?Array<number>,
  removedBedrooms: ?Array<number>,
  reinstated: false,
  movedToInProcess: boolean,
};

export default class AffordableWaitlistService {
  /**
   * Will retrieve all affordable waitlists for specific property
   * -
   * @param {*} organizationId
   * @param {*} propertyId
   * @returns
   */
  getAllAffordableWaitlistsByPropertyId(
    organizationId: string,
    propertyId: string,
    filters: Object,
    options?: Object,
  ): any {
    const filter = filters ? `filter=${JSON.stringify(filters)}` : '';
    const queryString = filter ? filter : '';
    return get(
      `/${organizationId}/${propertyId}/affordable-waitlist?${queryString}`,
      options,
    );
  }

  /**
   * Will get a affordable waitlist by application id
   * -
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} applicationId
   * @returns
   */
  getAffordableWaitlistByApplicationId(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist`,
      options,
    );
  }

  /**
   * Will add a household to the affordable waitlist
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  addToWaitlist(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    payload: AddToWaitlistPayloadType,
    options?: Object,
  ): any {
    return post(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Will add a note related to the affordable waitlist row
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  addWaitlistNote(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: AddWaitlistNotePayloadType,
    options?: Object,
  ): any {
    return post(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist/add-waitlist-note`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Will skip an application from the affordable waitlist
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  skipFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: SkipFromWaitlistPayloadType,
    options?: Object,
  ): any {
    return put(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist/skip-from-waitlist`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Will reinstate an application to the affordable waitlist
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  reinstateToWaitlist(
    applicationStatusId: string,
    applicationStatusIdModal: string,
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: ReinstateToWaitlistPayloadType,
    options?: Object,
  ): any {
    return put(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist/reinstate-to-waitlist`,
      JSON.stringify({
        ...payload,
        affordableWaitlistRow: {
          ...payload.affordableWaitlistRow,
          applicationStatusId: applicationStatusIdModal
            ? applicationStatusIdModal
            : applicationStatusId,
        },
      }),
      options,
    );
  }

  // Affordable Waitlist Property Preferences

  getAffordableWaitlistPropertyPreferences(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/affordable-waitlist/configure-preferences`,
      options,
    );
  }

  getApplicationsNeedingInfo(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): Promise<ApplicationsNeedingInfoResponseType> {
    return get(
      `/${organizationId}/${propertyId}/affordable-waitlist/applications-needing-info`,
      options,
    );
  }

  createAffordableWaitlistPropertyPreference(
    organizationId: string,
    propertyId: string,
    payload,
    options?: Object,
  ): any {
    const body = JSON.stringify(payload);
    return post(
      `/${organizationId}/${propertyId}/affordable-waitlist/configure-preferences`,
      body,
      options,
    );
  }

  updateAffordableWaitlistPropertyPreference(
    organizationId: string,
    propertyId: string,
    payload,
    options?: Object,
  ): any {
    const body = JSON.stringify(payload);
    return put(
      `/${organizationId}/${propertyId}/affordable-waitlist/configure-preferences`,
      body,
      options,
    );
  }

  /**
   * Will skip an application from the affordable waitlist
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  confirmFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: SkipFromWaitlistPayloadType,
    options?: Object,
  ): any {
    return put(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist/confirm-from-waitlist`,
      JSON.stringify(payload),
      options,
    );
  }

  removeFromWaitlist(
    organizationId: string,
    propertyId: string,
    payload,
    options?: Object,
  ): any {
    const body = JSON.stringify(payload);
    return patch(
      `/${organizationId}/${propertyId}/affordable-waitlist/applications/${payload.applicationId}`,
      body,
      options,
    );
  }

  denyApplicationFromWaitlist(
    organizationId: string,
    propertyId: string,
    payload,
    options,
  ) {
    const body = JSON.stringify(payload);
    return patch(
      `/${organizationId}/${propertyId}/affordable-waitlist/applications/${payload.applicationId}/deny`,
      body,
      options,
    );
  }

  generateAffordableWaitlistReport(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): any {
    return getDownload(
      `/${organizationId}/${propertyId}/affordable-waitlist/report`,
      `Affordable Wailist Report - ${moment().local().format('MM/DD/YYY')}.csv`,
      options,
    );
  }

  /**
   * Downloads the V2 waitlist report CSV, limited to one bedroom count (the
   * active tab) when given. getDownload rejects only on 400/500; any other
   * JSON error body (404 Property not found) resolves as `{ error }`, so it
   * is rejected here to reach the caller's error handling.
   */
  async generateAffordableWaitlistV2Report(
    organizationId: string,
    propertyId: string,
    bedroomCount: ?number,
    options?: Object,
  ): Promise<Blob> {
    const queryString =
      bedroomCount !== undefined && bedroomCount !== null
        ? `?bedroomCount=${bedroomCount}`
        : '';
    const result = await getDownload(
      `/${organizationId}/${propertyId}/affordable-waitlist-positions/report${queryString}`,
      `Affordable Waitlist Report - ${formatDateLocale(
        new Date(),
        'MM-DD-YYYY',
      )}.csv`,
      options,
    );
    if (result?.error) {
      throw new Error(result.error);
    }
    return result;
  }

  /**
   * Fetches AffordableWaitlistPosition rows for a property, optionally
   * filtered to a single bedroom count (the active waitlist tab) and/or a
   * status filter. Callers send a default status filter when the Include
   * All toggle is off; omitting `status` entirely (Include All on) returns
   * positions regardless of status. Left open as a free-form string so
   * specific status values/lists can be supported later.
   */
  getAffordableWaitlistPositions(
    organizationId: string,
    propertyId: string,
    bedroomCount: ?number,
    status: ?string,
    options?: Object,
  ): Promise<{ positions: Array<Object> }> {
    const params = [];
    if (bedroomCount !== undefined && bedroomCount !== null) {
      params.push(`bedroomCount=${bedroomCount}`);
    }
    if (status) {
      params.push(`status=${status}`);
    }
    const queryString = params.length ? `?${params.join('&')}` : '';
    return get(
      `/${organizationId}/${propertyId}/affordable-waitlist-positions${queryString}`,
      options,
    );
  }

  /**
   * Confirms or skips an applicant from a bedroom-grouped (V2) waitlist via
   * a single endpoint — replaces calling the separate confirm-from-waitlist
   * and skip-from-waitlist endpoints for V2. The payload's `reasons`/
   * `description` are included or omitted by the caller based on the
   * position's rank; this method itself has no rank-based branching.
   */
  selectFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: Object,
    options?: Object,
  ): Promise<SelectFromWaitlistResponseType> {
    return put(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist/select-from-waitlist`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Denies every AffordableWaitlistPosition for the applicant behind
   * affordableWaitlistId, per ENG-29579.
   */
  denyFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: AffordableWaitlistPositionActionPayloadType,
    options?: Object,
  ): Promise<DenyFromWaitlistResponseType> {
    return patch(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist-positions/deny`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Cancels every AffordableWaitlistPosition for the applicant behind
   * affordableWaitlistId, per ENG-29577.
   */
  cancelFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: AffordableWaitlistPositionActionPayloadType,
    options?: Object,
  ): Promise<CancelFromWaitlistResponseType> {
    return patch(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist-positions/cancel`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Reinstates every DENIED/CANCELLED AffordableWaitlistPosition for the
   * applicant behind applicationId back to WAITING, and saves the reviewed
   * waitlist values in the same call, per ENG-29631.
   */
  reinstateFromWaitlist(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    payload: Object,
    options?: Object,
  ): Promise<ReinstateFromWaitlistResponseType> {
    return patch(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist/reinstate`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Puts an existing applicant who has never been on the waitlist onto it,
   * taking the same body edit and reinstate take. Named apart from V1's
   * `addToWaitlist` above, which posts a different, flat payload to a
   * different route from the application status dropdown.
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} applicationId
   * @param {*} payload
   * @param {*} options
   * @returns
   */
  addToWaitlistV2(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    payload: Object,
    options?: Object,
  ): Promise<AddToWaitlistV2ResponseType> {
    return post(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist/add`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * The applicant's current waitlist values, in the same shape the edit and
   * reinstate PATCH bodies take. Feeds the wizard's prefill.
   */
  getWaitlistEditDetails(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    options?: Object,
  ): Promise<Object> {
    return get(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist/edit-details`,
      options,
    );
  }

  /**
   * Saves edited waitlist values for an applicant already on the waitlist.
   * A partial body: keys the property configuration hides are omitted, and a
   * key present with null is an explicit clear.
   */
  editAffordableWaitlist(
    organizationId: string,
    propertyId: string,
    applicationId: string,
    payload: Object,
    options?: Object,
  ): Promise<Object> {
    return patch(
      `/${organizationId}/${propertyId}/${applicationId}/affordable-waitlist`,
      JSON.stringify(payload),
      options,
    );
  }

  /**
   * Cancels every AffordableWaitlistPosition for the applicant behind
   * affordableWaitlistId while keeping the main Application status
   * In Process, per ENG-29636. Sibling of cancelFromWaitlist — identical
   * payload shape, different URL, different resulting application status.
   */
  cancelKeepInProcessFromWaitlist(
    organizationId: string,
    propertyId: string,
    affordableWaitlistId: string,
    payload: AffordableWaitlistPositionActionPayloadType,
    options?: Object,
  ): Promise<CancelFromWaitlistResponseType> {
    return patch(
      `/${organizationId}/${propertyId}/${affordableWaitlistId}/affordable-waitlist-positions/cancel-keep-in-process`,
      JSON.stringify(payload),
      options,
    );
  }
}
